import { Form, Head } from "@inertiajs/react"
import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"

import OnboardingAddressFieldset, {
  type OnboardingAddressFormValues,
} from "@/components/onboarding/onboarding-address-fieldset"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import { WizardAddressPrefilledNotice } from "@/components/wizard/wizard-address-prefilled-notice"
import type { WizardStepKey } from "@/components/wizard/wizard-progress"
import { WizardShell } from "@/components/wizard/wizard-shell"
import { useOnline } from "@/hooks/use-online"
import { onboardingAddressPath } from "@/routes"

export interface WizardAddressBrand {
  id?: number
  name: string
  logo_url: string | null
  locale: "en" | "fr"
}

export interface WizardAddressProps {
  brand: WizardAddressBrand
  /** Server-computed required steps. Address renders for every brand
   *  (P0 leak rule #2). */
  required_steps: readonly WizardStepKey[]
  /** Echoed values from BE — populated from `User.shipping_address` on
   *  the second-brand path; blank on first-brand. */
  form_values: { shipping_address?: OnboardingAddressFormValues }
  /** When true, render `<WizardAddressPrefilledNotice>` above the
   *  fieldset. Drives the visible affordance for the second-brand
   *  pre-fill path. */
  prefilled: boolean
}

/**
 * Wizard Step 3 — Shipping address capture / confirmation.
 *
 * Address page renders for EVERY brand (P0 leak rule #2: only TikTok
 * auto-skips on the second-brand path). The `<WizardAddressPrefilledNotice>`
 * is the second-brand affordance — informational alert above the fieldset
 * when `prefilled === true`. Field-level pre-fill comes from
 * `form_values.shipping_address` echoed by the server.
 *
 * Reuses `<OnboardingAddressFieldset>` (OKS-22) verbatim — same wire
 * format (`shipping_address[recipient_name|street|street_line_2|
 * postal_code|city|country]`) so the existing Vitest wire-format
 * canaries continue to apply.
 */
export default function Address({
  brand,
  required_steps,
  form_values,
  prefilled,
}: WizardAddressProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const headingRef = useRef<HTMLHeadingElement>(null)

  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  return (
    <WizardShell brand={brand} step="address" required_steps={required_steps}>
      <Head>
        <title>{t("wizard.address.head_title")}</title>
      </Head>
      <div data-testid="wizard-step-address" className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <h1
            ref={headingRef}
            tabIndex={-1}
            data-testid="wizard-step-address-heading"
            className="text-2xl font-bold tracking-tight outline-none"
          >
            {t("wizard.address.heading")}
          </h1>
          <p
            className="text-muted-foreground text-sm"
            data-testid="wizard-step-address-subtitle"
          >
            {t("wizard.address.subtitle", { brand: brand.name })}
          </p>
        </div>

        {prefilled && <WizardAddressPrefilledNotice brand_name={brand.name} />}

        <Form
          method="post"
          action={onboardingAddressPath()}
          className="flex flex-col gap-4"
          data-testid="wizard-address-form"
        >
          {({ processing, errors }) => {
            const baseErrors = errors.base
            const submitDisabled = processing || !online
            return (
              <>
                {Array.isArray(baseErrors) && baseErrors.length > 0 && (
                  <Alert
                    variant="destructive"
                    data-testid="wizard-address-error-base"
                  >
                    <AlertDescription>{baseErrors.join(", ")}</AlertDescription>
                  </Alert>
                )}

                <OnboardingAddressFieldset
                  values={form_values.shipping_address ?? {}}
                  errors={errors as Record<string, string[] | undefined>}
                  disabled={processing}
                />

                {!online && (
                  <p
                    className="text-muted-foreground text-sm"
                    role="status"
                    aria-live="polite"
                  >
                    {t("signup.errors.offline_helper")}
                  </p>
                )}

                <div className="bg-background sticky bottom-0 -mx-6 mt-2 flex flex-col gap-3 border-t px-6 py-4 sm:static sm:mx-0 sm:p-0">
                  <Button
                    type="submit"
                    disabled={submitDisabled}
                    aria-disabled={submitDisabled}
                    data-testid="wizard-address-submit"
                    size="lg"
                    className="w-full"
                  >
                    {processing && <Spinner className="size-4" />}
                    {processing
                      ? t("wizard.address.submit_processing")
                      : t("wizard.address.submit")}
                  </Button>
                </div>
              </>
            )
          }}
        </Form>
      </div>
    </WizardShell>
  )
}
